import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { API, graphqlOperation } from 'aws-amplify';
import Floater from 'react-floater';
import { Sparkline, DeltaPill } from '../../../../components';
import {
  getIOTDocument,
  onUpdateIOTDocument,
} from '../../../../graphql/iot-documents';
import {
  getTotalEnergyGenerated,
  initiateAplicacaoEnergy,
} from '../../../../services';
import {
  formatValue,
  formatEnergyAutoUnit,
  formatPowerAutoUnit,
  notifyError,
  createReconnectingSubscription,
} from '../../../../utils';
import { themes } from '../../../../assets';
import {
  isBlueboxThings,
  extractBlueboxAlarms,
} from '../../../../constants/bluebox';
import { applySimulator } from '../../../../utils/simulator';
import { useSimulator } from '../../../../contexts/simulator';
import * as s from './styled-info-aplicacao';

// Tamanho do buffer rolling da serie ao vivo exibida no sparkline do Hero.
// 30 pontos a cada update da subscription da uma janela visualmente util
// sem segurar muita memoria. A serie nao precisa ser persistente.
const LIVE_BUFFER_SIZE = 30;

// Identifica variaveis de alarme no payload da subscription IoT. O backend
// usa tres familias de prefixos. Tratamos truthy/numero>0 como alarme ativo.
const ALARM_FIELD_PREFIXES = ['AdvertenciaWord', 'BloqueioWord', 'AlarmeWord'];

const extractActiveAlarms = (thingData) => {
  if (!thingData || typeof thingData !== 'object') return [];
  const hits = [];
  Object.keys(thingData).forEach((key) => {
    if (ALARM_FIELD_PREFIXES.some((p) => key.startsWith(p))) {
      const v = thingData[key];
      // Word fields tipicamente sao bitmasks; tratamos qualquer valor !== 0
      // como "ha alarme nessa word". Detalhamento bit-a-bit fica pra futuro.
      if (v && v !== 0 && v !== '0') hits.push(key);
    }
  });
  // ErroAtivo / FalhaAtiva como boolean flag
  if (thingData.ErroAtivo) hits.push('ErroAtivo');
  if (thingData.FalhaAtiva) hits.push('FalhaAtiva');
  return hits;
};

const InformacoesAplicacao = ({ selectedProduto, type, things }) => {
  // BLUEBOX (thing type_id 3, dentro de uma EBA) publica grandezas proprias
  // — os KPIs do Resumo leem os campos MQTT correspondentes dela.
  const isBluebox = isBlueboxThings(things);
  const [rawThingsDataInfo, setThingsDataInfo] = useState([]);
  const sharedIotData = useSelector((state) => state.aplicacao.velocimetro);
  const sourceThingsData = useMemo(
    () =>
      isBluebox
        ? things.map(({ id }) => sharedIotData[id]).filter(Boolean)
        : rawThingsDataInfo,
    [isBluebox, things, sharedIotData, rawThingsDataInfo]
  );
  // Modo simulador (so dev): preenche com dados fake pra visualizar KPIs
  // sem telemetria real. Desligado === dado real.
  const { enabled: simulatorEnabled } = useSimulator();
  const thingsDataInfo = useMemo(
    () => applySimulator(sourceThingsData, things, simulatorEnabled),
    [sourceThingsData, things, simulatorEnabled]
  );
  const [thingsLoaded, setThingsLoaded] = useState(false);
  const dispatch = useDispatch();
  // Seletor especifico: state.aplicacao tem realtimeData/velocimetro/etc
  // que atualizam frequentemente via subscription. Selecionando so .energy
  // evitamos re-render quando os outros campos mudam.
  const energy = useSelector((state) => state.aplicacao.energy);
  const socketRef = useRef(null);
  const [totalEnergy, setTotalEnergy] = useState(null);
  const [onTime, setOnTime] = useState(null);
  // Buffer rolling da potencia agregada — alimenta o sparkline live do Hero.
  // E so client-side, nao persiste. Reseta quando a aplicacao muda.
  const [powerSeries, setPowerSeries] = useState([]);

  // Busca dados iniciais de cada thing + abre subscription pra updates em
  // tempo real. Refatorado para evitar a race condition do codigo anterior:
  // - acumulador unico (acc) em vez de dois arrays paralelos
  // - mountedRef gating de setState para evitar update apos unmount
  // - cleanup unsubscribe de TODAS as subscriptions criadas (antes dependia
  //   de uma variavel mutavel que podia perder referencias)
  // - sem setState sincrono no fim do effect que rodava antes dos fetches
  useEffect(() => {
    if (isBluebox) {
      setThingsLoaded(true);
      return undefined;
    }
    let mounted = true;
    const recons = [];
    const acc = [];

    setThingsLoaded(false);

    Promise.all([getIOTDocument(), onUpdateIOTDocument()]).then(
      ([iotQuery, updateIotSubscription]) => {
        if (!mounted) return;

        const refreshThing = async ({ thing }, index) => {
          try {
            const response = await API.graphql(
              graphqlOperation(iotQuery, { thingName: thing })
            );
            const iot =
              'getIOT' in response ? response.getIOT : response.data?.getIOT;

            if (!iot || !mounted) return;
            acc[index] = iot;
            setThingsDataInfo([...acc]);
          } catch (_) {
            // A subscription continua ativa e o proximo ciclo tenta novamente.
          }
        };

        things.forEach(async ({ thing }, index) => {
          try {
            await refreshThing({ thing }, index);
            if (!acc[index]) {
              notifyError('Não foi possível buscar as informações da Thing');
              return;
            }
            if (!mounted) return;

            // createReconnectingSubscription com backoff exponencial garante
            // que a tela nao fique com dados congelados se a conexao cair.
            const recon = createReconnectingSubscription({
              label: `informacoes-aplicacao/${thing}`,
              buildSubscription: ({ next, error }) =>
                API.graphql(
                  graphqlOperation(updateIotSubscription, { thingName: thing })
                ).subscribe({ next, error }),
              onNext: ({ value }) => {
                const update = value && value.data && value.data.onUpdateIOT;
                if (!update || !mounted) return;
                acc[index] = update;
                setThingsDataInfo([...acc]);
              },
            });
            recons.push(recon);
          } catch (e) {
            // erro silencioso aqui — o notifyError ja cuidou do caso comum
          }
        });
      }
    );

    // Marca loaded depois que o forEach disparou todas as promises
    // (em paralelo). Os setStates de cada chunk ocorrem conforme resolvem.
    Promise.resolve().then(() => {
      if (mounted) setThingsLoaded(true);
    });

    return () => {
      mounted = false;
      recons.forEach((r) => {
        if (r && typeof r.unsubscribe === 'function') r.unsubscribe();
      });
    };
  }, [things, isBluebox]);

  useEffect(() => {
    let mounted = true;
    if (selectedProduto && !energy) {
      if (
        socketRef.current &&
        typeof socketRef.current.disconnect === 'function'
      ) {
        socketRef.current.disconnect();
      }
      getTotalEnergyGenerated(selectedProduto.id)
        .then(({ data }) => {
          dispatch({
            type: 'APLICACAO/SET_ENERGY',
            data,
          });
          if (mounted) {
            socketRef.current = initiateAplicacaoEnergy(selectedProduto.id);
          }
        })
        .catch(() => notifyError('Houve um erro ao buscar as informações'));
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduto, dispatch]);

  // Recalcula totais sempre que thingsDataInfo muda. null = ainda nao
  // recebeu dados (mostra "—" na UI em vez de 0 enganoso).
  useEffect(() => {
    if (!thingsDataInfo.length) {
      setTotalEnergy(null);
      setOnTime(null);
      return;
    }
    // Net energy por tipo de maquina, garantindo positivo em operacao normal:
    //   - UCHA (turbogerador): Reversa - Ativa  (entregue a rede - consumido)
    //   - EBA  (bomba):        Ativa - Reversa  (consumido - entregue a rede)
    // Negativo => anomalia (UCHA consumindo da rede ou EBA gerando).
    const netEnergyFor = (thing) => {
      // BLUEBOX: energia consumida = ActE (direta) - RevE (reversa).
      if (isBluebox) {
        return (Number(thing.ActE) || 0) - (Number(thing.RevE) || 0);
      }
      const ativa = thing.EnergiaAtiva || 0;
      const reversa = thing.EnergiaAtivaReversa || 0;
      return type === 'eba' ? ativa - reversa : reversa - ativa;
    };
    const totals = thingsDataInfo.reduce(
      (sum, thing) => {
        if (!thing) return sum;
        sum.energy += netEnergyFor(thing);
        // BLUEBOX: potencia ativa total em W -> kW. Demais: PotenciaAtivaMedia
        // ja vem em kW.
        sum.ontime += isBluebox
          ? (Number(thing.W) || 0) / 1000
          : thing.PotenciaAtivaMedia || 0;
        return sum;
      },
      { energy: 0, ontime: 0 }
    );
    setTotalEnergy(totals.energy);
    setOnTime(totals.ontime);
    // Empurra o totals.ontime no buffer rolling pra alimentar o sparkline.
    // Mantemos so LIVE_BUFFER_SIZE pontos — slice no fim e mais barato que
    // shift no comeco.
    setPowerSeries((prev) => {
      const next = [...prev, { value: totals.ontime, ts: Date.now() }];
      return next.length > LIVE_BUFFER_SIZE
        ? next.slice(next.length - LIVE_BUFFER_SIZE)
        : next;
    });
  }, [thingsDataInfo, type, isBluebox]);

  // Reseta o buffer rolling quando muda a aplicacao — evita misturar series
  // de aplicacoes diferentes ao navegar.
  useEffect(() => {
    setPowerSeries([]);
  }, [selectedProduto.id]);

  // Eficiencia energetica derivada (kWh equivalente por m³). Calculada
  // localmente da subscription: Σ Potencia / Σ Vazao. Util como indicador
  // instantaneo de "quanta energia gastamos pra bombear cada m³" (EBA) ou
  // "quanta energia geramos por m³ turbinado" (UCHA).
  const efficiency = useMemo(() => {
    if (!thingsDataInfo.length) return null;
    let totalPower = 0;
    let totalFlow = 0;
    thingsDataInfo.forEach((t) => {
      if (!t) return;
      const p = Number(t.PotenciaAtivaMedia);
      const f = Number(t.Vazao);
      if (Number.isFinite(p)) totalPower += Math.abs(p);
      if (Number.isFinite(f)) totalFlow += f;
    });
    // Sem vazao -> indefinido (evita divisao por zero gigante).
    if (totalFlow <= 0) return null;
    // Vazao vem em l/s — converte pra m³/s pra ter kW por m³/s = kWh/m³
    // (potencia em kW, vazao em m³/s => energia equivalente por unidade
    // de volume bombeado).
    const flowM3s = totalFlow / 1000;
    return totalPower / flowM3s;
  }, [thingsDataInfo]);

  // BLUEBOX: nao publica vazao, entao nao ha "eficiencia kWh/m3". No lugar
  // mostramos o Fator de Potencia (FP) — indicador de qualidade do consumo
  // eletrico. Media das maquinas que reportam FP.
  const blueboxFP = useMemo(() => {
    if (!isBluebox || !thingsDataInfo.length) return null;
    let sum = 0;
    let n = 0;
    thingsDataInfo.forEach((t) => {
      const v = Number(t && t.FP);
      if (Number.isFinite(v)) {
        sum += v;
        n += 1;
      }
    });
    return n ? sum / n : null;
  }, [isBluebox, thingsDataInfo]);

  // BLUEBOX: horimetro (Hor, horas de operacao). Soma das maquinas — pra
  // 1 maquina e o proprio valor; pra varias, total de horas-maquina.
  const blueboxHor = useMemo(() => {
    if (!isBluebox || !thingsDataInfo.length) return null;
    let sum = 0;
    let has = false;
    thingsDataInfo.forEach((t) => {
      const v = Number(t && t.Hor);
      if (Number.isFinite(v)) {
        sum += v;
        has = true;
      }
    });
    return has ? sum : null;
  }, [isBluebox, thingsDataInfo]);

  // Atividade da sessao — % das amostras recentes em que houve potencia
  // ativa significativa (> 1% do pico observado, e > 0.5 kW absoluto pra
  // descartar ruido). E uma aproximacao do uptime, limitada a janela do
  // buffer rolling (LIVE_BUFFER_SIZE pontos).
  //
  // TODO(analytics #5): Disponibilidade REAL precisa de endpoint backend
  // que retorne historico de estado operacional (running/stopped) por
  // intervalo de tempo. Ex.: GET /api/v1/appliance/:id/availability?period=day
  // retornando { uptimeSeconds, totalSeconds, transitions: [...] }. Com isso
  // substituir essa derivacao por uptime de 24h verdadeiro + MTBF.
  const sessionActivity = useMemo(() => {
    if (powerSeries.length < 3) return null;
    const absValues = powerSeries.map((p) => Math.abs(p.value));
    const peak = Math.max(...absValues);
    if (peak <= 0) return null;
    const threshold = Math.max(peak * 0.01, 0.5);
    const active = absValues.filter((v) => v > threshold).length;
    return (active / absValues.length) * 100;
  }, [powerSeries]);

  // Lista de alarmes ativos por maquina — alimenta o Floater no card
  // "Alarme/Erro" para que o operador veja QUAL maquina e QUAL alarme,
  // nao so um contador.
  const activeAlarms = useMemo(() => {
    if (!thingsDataInfo.length) return [];
    return thingsDataInfo
      .map((data, idx) => {
        // BLUEBOX: alarmes/trips vem nas words Alr1/Alr2/Trp1/Trp2
        // (decodificadas por bit); demais maquinas usam *Word/ErroAtivo.
        const alarms = isBluebox
          ? extractBlueboxAlarms(data)
          : extractActiveAlarms(data);
        if (alarms.length === 0) return null;
        const machine = things[idx];
        return {
          machineName: (machine && machine.thing) || `Máquina ${idx + 1}`,
          alarms,
        };
      })
      .filter(Boolean);
  }, [thingsDataInfo, things, isBluebox]);

  const energiaLabel = type === 'ucha' ? 'gerada' : 'consumida';
  const hasError = selectedProduto.status && selectedProduto.status.E > 0;
  const alarmCount =
    activeAlarms.length > 0
      ? activeAlarms.length
      : (selectedProduto.status && selectedProduto.status.E) || 0;
  // Loading do bloco "Máquinas / Total / Ontime" depende de things ter
  // resolvido + totais ja calculados.
  const metricsLoading = !thingsLoaded || totalEnergy === null;
  // Para EBA a metrica "menos consumo = melhor" — invertSemantic no DeltaPill.
  const efficiencyInvertSemantic = type === 'eba';

  // Helper para separar valor formatado e unidade — permite renderizar a
  // unidade com tipografia menor (<small>) mantendo escalonamento auto.
  const splitFormatted = (formatted) => {
    if (typeof formatted !== 'string') return { value: '—', unit: '' };
    const idx = formatted.lastIndexOf(' ');
    if (idx === -1) return { value: formatted, unit: '' };
    return { value: formatted.slice(0, idx), unit: formatted.slice(idx + 1) };
  };

  // Modo simulador: sem energia real por periodo, injeta valores fake nos
  // cards Mes/Semana/Dia (day < week < mes). Dado real sempre vence.
  const displayEnergy = useMemo(() => {
    if (!simulatorEnabled) return energy;
    const real = energy && energy.totalEnergyGenerated;
    const hasReal = real && Object.values(real).some((v) => Number(v) > 0);
    if (hasReal) return energy;
    return {
      ...(energy || {}),
      totalEnergyGenerated: { day: 3200, week: 21500, mounth: 92000 },
      previousTotalEnergyGenerated: { day: 3000, week: 20400, mounth: 88500 },
    };
  }, [energy, simulatorEnabled]);

  const totalEnergyParts = metricsLoading
    ? { value: '—', unit: '' }
    : splitFormatted(formatEnergyAutoUnit(totalEnergy, 'kWh'));
  const onTimeParts = metricsLoading
    ? { value: '—', unit: '' }
    : splitFormatted(formatPowerAutoUnit(onTime, 'kW'));

  return (
    <s.Container>
      {/* Linha unica de KPIs alinhados: primario (Total Energia) + 4
          secundarios (Maquinas, Ontime, Eficiencia, Alarme). Sem cards
          aninhados — hierarquia puramente tipografica + divisor vertical
          sutil entre KPIs. */}
      <s.TopRow>
        {!isBluebox && (
          <s.PrimaryKPI accent={type}>
            <s.PrimaryLabel
              title={`Energia ${energiaLabel} acumulada (total) desde o inicio do monitoramento — soma liquida de todas as maquinas.`}
            >
              Total Energia {energiaLabel}
            </s.PrimaryLabel>
            <s.PrimaryValue
              color={
                metricsLoading
                  ? themes.label
                  : totalEnergy < 0
                  ? themes.statusErro
                  : themes.green
              }
            >
              {totalEnergyParts.value}
              {!metricsLoading && <small>{totalEnergyParts.unit}</small>}
            </s.PrimaryValue>
            {powerSeries.length >= 2 && (
              <s.PrimarySparklineWrapper title="Potência total agregada (tempo real)">
                <Sparkline
                  data={powerSeries}
                  width={140}
                  height={22}
                  color={totalEnergy < 0 ? themes.statusErro : themes.green}
                  fill
                />
                <s.PrimaryMeta>
                  <s.PrimaryMetaLabel>Potência ao vivo</s.PrimaryMetaLabel>
                </s.PrimaryMeta>
              </s.PrimarySparklineWrapper>
            )}
          </s.PrimaryKPI>
        )}

        {/* KPI "Maquinas" movido pra um chip ao lado do tag "Resumo"
            (appliance.js), usando a contagem real de produto.thing —
            evita o energy.thingsAmount inconsistente do socket. */}
        <s.SecondaryKPI>
          <s.KPILabel>
            Potência ativa
            <Floater
              event="hover"
              content={
                <label style={{ color: '#1f2937' }}>
                  <span style={{ fontWeight: 800 }}>VALORES NEGATIVOS</span>:
                  representam geração/devolução de energia elétrica para a rede
                  (função gerador/turbina)
                  <br />
                  <span style={{ fontWeight: 800 }}>VALORES POSITIVOS</span>:
                  representam consumo de energia elétrica da rede (função
                  motor/bomba)
                </label>
              }
              styles={{
                container: {
                  padding: 10,
                  borderRadius: 12,
                  fontSize: 12,
                  width: 460,
                  color: '#1f2937',
                  lineHeight: 1.5,
                },
              }}
            >
              <s.Info />
            </Floater>
          </s.KPILabel>
          <s.KPIValue color={metricsLoading ? themes.label : themes.green}>
            {onTimeParts.value}
            {!metricsLoading && <small>{onTimeParts.unit}</small>}
          </s.KPIValue>
          {sessionActivity !== null && (
            <s.KPISubValue title="Percentual das amostras desta sessão de monitoramento com potência ativa (proxy de uptime — real 24h precisa endpoint backend)">
              ativo {sessionActivity.toFixed(0)}% (sessão)
            </s.KPISubValue>
          )}
        </s.SecondaryKPI>

        <s.SecondaryKPI>
          <s.KPILabel>
            {isBluebox ? 'Fator de potência' : 'Eficiência'}
            <Floater
              event="hover"
              content={
                isBluebox ? (
                  <label style={{ color: '#1f2937' }}>
                    Fator de potência total (FP):
                    <br />
                    razão entre potência ativa e aparente (0 a 1).
                    <br />
                    Mais próximo de 1 = consumo elétrico mais eficiente.
                  </label>
                ) : (
                  <label style={{ color: '#1f2937' }}>
                    Energia por volume bombeado/turbinado:
                    <br />
                    <strong>kWh por m³</strong>
                    <br />
                    Calculado como ΣPotência (kW) ÷ ΣVazão (m³/s).
                    <br />
                    Para EBA, valores menores = mais eficiente.
                    <br />
                    Para UCHA, valores maiores = mais energia gerada por m³.
                  </label>
                )
              }
              styles={{
                container: {
                  padding: 10,
                  borderRadius: 12,
                  fontSize: 12,
                  width: 340,
                  color: '#1f2937',
                  lineHeight: 1.5,
                },
              }}
            >
              <s.Info />
            </Floater>
          </s.KPILabel>
          <s.KPIValue color={metricsLoading ? themes.label : themes.darkerblue}>
            {isBluebox
              ? blueboxFP === null || metricsLoading
                ? '—'
                : formatValue(blueboxFP)
              : efficiency === null || metricsLoading
              ? '—'
              : formatValue(efficiency)}
            {!isBluebox && efficiency !== null && !metricsLoading && (
              <small>kWh/m³</small>
            )}
          </s.KPIValue>
        </s.SecondaryKPI>

        <s.SecondaryKPI>
          <s.KPILabel>
            Alarme/Erro
            {activeAlarms.length > 0 && (
              <Floater
                event="hover"
                content={
                  <s.AlarmList>
                    <s.AlarmListTitle>
                      Alarmes ativos ({activeAlarms.length})
                    </s.AlarmListTitle>
                    {activeAlarms.map(({ machineName, alarms }) => (
                      <s.AlarmListItem key={machineName}>
                        <s.AlarmListMachine>{machineName}</s.AlarmListMachine>
                        <s.AlarmListVars>
                          {alarms.slice(0, 4).join(', ')}
                          {alarms.length > 4 && ` +${alarms.length - 4}`}
                        </s.AlarmListVars>
                      </s.AlarmListItem>
                    ))}
                  </s.AlarmList>
                }
                styles={{
                  container: {
                    padding: 0,
                    borderRadius: 12,
                    fontSize: 12,
                    width: 340,
                    overflow: 'hidden',
                  },
                }}
              >
                <s.Info />
              </Floater>
            )}
          </s.KPILabel>
          <s.KPIValue
            /* Vermelho padrao do sistema (--cco-red #f4445c) quando ha
               alarme/erro — mesmo token do status critico no resto do
               CCO. Antes usava themes.statusErro (#EB6D70, palette legada
               dessaturada) que destoava. Idle em --cco-ink-mute. */
            color={
              alarmCount > 0 || hasError
                ? 'var(--cco-red)'
                : 'var(--cco-ink-mute)'
            }
          >
            {alarmCount}
          </s.KPIValue>
        </s.SecondaryKPI>

        {/* BLUEBOX: horimetro (horas de operacao acumuladas) — "quanto
            tempo o equipamento rodou". So aparece pra bluebox. */}
        {isBluebox && (
          <s.SecondaryKPI>
            <s.KPILabel>Horímetro</s.KPILabel>
            <s.KPIValue color={metricsLoading ? themes.label : themes.green}>
              {blueboxHor === null || metricsLoading
                ? '—'
                : blueboxHor.toLocaleString('pt-BR', {
                    maximumFractionDigits: 0,
                  })}
              {blueboxHor !== null && !metricsLoading && <small>h</small>}
            </s.KPIValue>
          </s.SecondaryKPI>
        )}
      </s.TopRow>

      {!isBluebox && <s.Divider />}

      {!isBluebox && (
        <s.PeriodRow>
          {/* Label "ENERGIA GERADA POR PERÍODO" wrapped na propria celula
            bordered pra alinhar visualmente com as 3 celulas de periodo
            (Mes/Semana/Dia) no padrao hairline. */}
          <s.PeriodLabelCell>
            <s.PeriodSectionLabel>
              Energia {energiaLabel} por período
            </s.PeriodSectionLabel>
          </s.PeriodLabelCell>
          {[
            { key: 'mounth', label: 'Mês' },
            { key: 'week', label: 'Semana' },
            { key: 'day', label: 'Dia' },
          ].map(({ key, label }) => {
            const value =
              displayEnergy && displayEnergy.totalEnergyGenerated
                ? displayEnergy.totalEnergyGenerated[key]
                : null;
            // Tenta varios formatos de payload onde o backend possa estar
            // retornando o periodo anterior (defensivo — DeltaPill devolve
            // null se nada for encontrado, entao a UI degrada graciosamente).
            const prevContainer =
              (displayEnergy && displayEnergy.previousTotalEnergyGenerated) ||
              (displayEnergy && displayEnergy.previousPeriod) ||
              null;
            const previousValue = prevContainer ? prevContainer[key] : null;
            // Numericos limpos para o DeltaPill — strings com unidade ja
            // formatada nao servem pra calculo de delta.
            const numericValue =
              typeof value === 'number'
                ? value
                : typeof value === 'string'
                ? parseFloat(value)
                : null;
            const numericPrev =
              typeof previousValue === 'number'
                ? previousValue
                : typeof previousValue === 'string'
                ? parseFloat(previousValue)
                : null;
            // Regra de produto: UCHA SO gera, EBA SO consome — a label do
            // card e a semantica fixa do tipo. Quando o backend retorna
            // net negativo (UCHA idle / EBA com anomalia), mostramos o
            // valor ABSOLUTO + cor vermelha como indicador de anomalia.
            // NAO usamos sufixo '(consumiu)' / '(gerou)' porque contradiz
            // a label do tipo ("ENERGIA GERADA" + "(consumiu)" e confuso).
            // A cor vermelha + DeltaPill ▼ ja comunicam a degradacao.
            const isAnomaly = typeof value === 'number' && value < 0;
            const displayValue =
              typeof value === 'number'
                ? isAnomaly
                  ? Math.abs(value)
                  : value
                : value;
            return (
              <s.PeriodCell key={key}>
                <s.PeriodLabel>{label}</s.PeriodLabel>
                <s.PeriodValue negative={isAnomaly}>
                  {displayValue === null || displayValue === undefined
                    ? '—'
                    : formatEnergyAutoUnit(displayValue, 'kWh')}
                </s.PeriodValue>
                <DeltaPill
                  current={numericValue}
                  previous={numericPrev}
                  invertSemantic={efficiencyInvertSemantic}
                  small
                />
              </s.PeriodCell>
            );
          })}
        </s.PeriodRow>
      )}
    </s.Container>
  );
};

export default InformacoesAplicacao;
